import React from 'react';
import { ArrowRight, Clock } from 'lucide-react';
import { DateList, DayView } from './';
import { ClassData, SignupData } from '../../types';
import { formatDate, formatTime } from '../../helpers';

interface CalendarViewProps {
    dates: Date[];
    selectedDay: Date;
    setSelectedDay: (date: Date) => void;
    classesByDate: Record<string, ClassData[]>;
    isMobileView: boolean;
    onDateClick: () => void;
    classesForSelectedDay: ClassData[];
    signupsByClassId: Record<string, SignupData[]>;
    getUserStatus: (cls: ClassData) => 'booked' | null;
    openShareModal: (type: 'date' | 'class', id: string, name?: string) => void;
    nextUpcomingClass: ClassData | null;
    handleBookingStart: (cls: ClassData) => void;
    onManageBooking: (cls: ClassData) => void;
    // For the Next Class Banner
    onBannerClick: (cls: ClassData) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({
    dates, selectedDay, setSelectedDay, classesByDate, isMobileView, onDateClick,
    classesForSelectedDay, signupsByClassId, getUserStatus, openShareModal, 
    nextUpcomingClass, handleBookingStart, onManageBooking, onBannerClick
}) => {
    return (
        <React.Fragment>
          {/* --- NEW: NEXT CLASS BANNER --- */}
          {nextUpcomingClass && (
             <div 
               onClick={() => onBannerClick(nextUpcomingClass)}
               className="w-full bg-gradient-to-r from-fuchsia-900/60 to-rose-900/60 border border-fuchsia-500/30 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4 cursor-pointer hover:border-fuchsia-400/50 transition-all group shadow-xl mb-6"
             >
                <div className="flex items-center gap-3">
                   <div className="bg-rose-500 p-2 rounded-full text-white shadow-lg shadow-rose-900/50">
                      <Clock size={20} />
                   </div>
                   <div>
                      <div className="text-xs text-rose-300 font-bold uppercase tracking-wider mb-0.5">Next Upcoming Class</div>
                      <div className="text-lg font-serif text-white group-hover:text-rose-200 transition-colors">
                          {nextUpcomingClass.name} <span className="text-slate-400 text-base">with {nextUpcomingClass.instructor}</span>
                      </div>
                      <div className="text-sm text-slate-300">
                          {formatDate(new Date(nextUpcomingClass.date + 'T12:00:00'))} • {formatTime(nextUpcomingClass.startTime)}
                      </div>
                   </div>
                </div>
                <div className="flex items-center gap-2 text-fuchsia-300 group-hover:text-white text-sm font-bold bg-white/5 px-4 py-2 rounded-lg">
                   Register Now <ArrowRight size={16} />
                </div>
             </div>
          )}
  
          <div className="grid md:grid-cols-12 gap-6">
            {/* LEFT COLUMN: DATE LIST */}
            <div className="col-span-12 md:col-span-4 lg:col-span-3">
               <DateList 
                  dates={dates} 
                  selectedDay={selectedDay} 
                  setSelectedDay={setSelectedDay} 
                  classesByDate={classesByDate} 
                  isMobileView={isMobileView} 
                  onDateClick={onDateClick} 
               />
            </div>

            {/* RIGHT COLUMN: CLASS DETAILS (DESKTOP ONLY) */}
            {!isMobileView && (
                <div className="md:col-span-8 lg:col-span-9">
                    <DayView 
                        selectedDay={selectedDay} 
                        classesForSelectedDay={classesForSelectedDay} 
                        signupsByClassId={signupsByClassId} 
                        getUserStatus={getUserStatus} 
                        openShareModal={openShareModal} 
                        nextUpcomingClass={nextUpcomingClass} 
                        setSelectedDay={setSelectedDay} 
                        handleBookingStart={handleBookingStart}
                        onManageBooking={onManageBooking} 
                    />
                </div>
            )}
          </div>
        </React.Fragment>
    );
};

export default CalendarView;
